import { useEffect, useState } from "react";
import { Row, message, Pagination } from "antd";
import { useDispatch } from "react-redux";

import Upload from "@components/Upload";
import Table from "@components/Table";
import { getVersionsColumns } from "./config";
import {
  ErrorServices,
  marketDataApi,
  useFetchMarketDataVersionsQuery,
  useUpdateMarketDataVersionMutation,
  useUploadMarketDataMutation,
} from "@services";
import { IVersion } from "@types";
import { useTypedSelector } from "@hooks";
import { checkPermission } from "@/utils";
import { permissions } from "@/router";
import { MdOutlineFileDownload, MdOutlineFileUpload } from "react-icons/md";
import CustomButton from "@/components/customButton";
import SearchBox from "@/components/searchInput";
import customErrorMessage from "@/components/CustomErrorMessage";

const MarketData = () => {
  const { company } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;
  const dispatch = useDispatch();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data: versions, isLoading } = useFetchMarketDataVersionsQuery({
    page: currentPage,
    company_id,
    page_size: pageSize,
  });

  const [updateMarketDataVersion] = useUpdateMarketDataVersionMutation();
  const [uploadMarketData, { isLoading: isUploading }] =
    useUploadMarketDataMutation();

  const { data = [], pagination } = versions || {};
  const totalItems = pagination?.count || 0;

  const [isUploadModal, setIsUploadModal] = useState(false);
  const [currentId, setCurrentId] = useState<null | number>(null);
  const [searchInput, setSearchInput] = useState("");
  const [uploadFile, setUploadFile] = useState({
    file: null,
    active: false,
  });

  // Select current active version
  useEffect(() => {
    if (data?.length) {
      const activeVersion = data.find(
        (version: IVersion) => version?.is_active === "TRUE"
      );
      if (activeVersion) setCurrentId(activeVersion.id);
    }
  }, [data]);

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  const handleActive = async (id: number) => {
    try {
      setCurrentId(id);
      await updateMarketDataVersion({ id, company_id }).unwrap();
    } catch (error) {
      ErrorServices(error);
    }
  };

  const handleUpload = async () => {
    const { file, active } = uploadFile;
    setIsUploadModal(false);
    try {
      const formData = new FormData();
      formData.append("attachment", file!, file?.name!);
      const res = await uploadMarketData({
        company_id,
        active,
        body: formData,
      }).unwrap();
      message.success(res?.message);
    } catch (error) {
      customErrorMessage(error?.message);
    }
  };

  const download = (id?: number) => {
    dispatch(
      marketDataApi.endpoints.downloadMarketData.initiate(
        {
          company_id,
          version_id: id || null,
        },
        {
          subscribe: false,
          forceRefetch: true,
        }
      )
    );
  };

  const versionsColumns = getVersionsColumns({
    handleActive,
    currentId,
    download,
  });

  return (
    <>
      {isUploadModal && (
        <Upload
          title="Upload Internal Payroll Data"
          onSubmit={handleUpload}
          isVisible={isUploadModal}
          setIsVisible={setIsUploadModal}
          file={uploadFile}
          setFile={setUploadFile}
        />
      )}

      <div className="bg-themeCore">
        <div className="jobCreationBar bg-white px-4 py-6 rounded-lg flex justify-between items-center">
          <div className="title text-themeBlue font-medium text-3xl">
            Market Data
          </div>

          <div className="flex flex-row justify-between">
            {(checkPermission(permissions.CREATE_MARKET_DATA) ||
              checkPermission(permissions.UPDATE_MARKET_DATA)) && (
              <CustomButton
                isLoading={isUploading}
                onClick={() => setIsUploadModal(true)}
                btnName="Upload"
                customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder"
                iconPosition="left"
              >
                <MdOutlineFileUpload
                  size={20}
                  className="text-white font-bold"
                />
              </CustomButton>
            )}

            <CustomButton
              onClick={() => download()}
              btnName="Download"
              customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
              iconPosition="right"
            >
              <MdOutlineFileDownload
                size={20}
                className="text-white font-bold"
              />
            </CustomButton>
          </div>
        </div>
      </div>

      <div className="jobCreationListing mt-6 bg-white px-14 py-4 rounded-lg">
        <SearchBox
          placeholder="Type to search..."
          value={searchInput}
          onChange={value => setSearchInput(value)}
        />

        <Row>
          <Table
            isLoading={isLoading}
            data={data}
            columns={versionsColumns}
            pagination={true} // External Pagination only
            count={pagination?.count}
            onChangePage={page => setCurrentPage(page)}
            onPageSizeChange={setPageSize}
            page={currentPage}
          />
        </Row>

        {/* <div className="flex justify-center mt-6">
          <Pagination
            current={currentPage}
            total={totalItems}
            pageSize={10} // should match backend
            onChange={page => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </div> */}
      </div>
    </>
  );
};

export default MarketData;
